/**
 * REPORTS API CLIENT
 *
 * All API calls for the reporting module. Uses JWT authentication via Jira Connect.
 *
 * Note: date-range filtering has been removed. Reports are driven by scope
 * (project / release / scenario) + optional filter criteria (status, priority,
 * assignee, labels, releases).
 */

import axios from 'axios'
import { BASE_URL } from '../constants/config'
import { getJwtToken, clearJwtTokenCache } from '../utils/jiraContext'
import { emitLicenseRequired } from '../utils/licenseManager'

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  // Reports aggregate data server-side; give them extra headroom.
  timeout: 20000
})

apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getJwtToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      } else {
        console.warn('Reports API request sent without JWT token')
      }
    } catch (error) {
      console.error('Failed to get JWT token for reports API:', error)
    }
    return config
  },
  (error) => Promise.reject(error)
)

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Reports API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    })

    if (error.response?.status === 402) {
      emitLicenseRequired(error.response?.data?.message || 'License required')
    }
    if (error.response?.status === 401) {
      console.error('Unauthorized access to reports API - clearing token cache')
      clearJwtTokenCache()
    }

    return Promise.reject(error)
  }
)

// ============================================================================
// FILTER PARAM HELPER
// ============================================================================

/**
 * Serialize a filter object into URLSearchParams.
 *
 * Each list field is sent as repeated query params (Spring's `@ModelAttribute`
 * binds repeated values into List<>). Empty / null lists are skipped so the
 * server treats them as "no filter".
 *
 * @param {object} filters
 * @param {string[]} [filters.statuses]
 * @param {string[]} [filters.priorities]
 * @param {Array<number|string>} [filters.assigneeIds]
 * @param {Array<number|string>} [filters.labelIds]
 * @param {Array<number|string>} [filters.releaseIds]
 * @param {object} [extra] - optional extra scalar params (e.g. includeRunBreakdown)
 * @returns {string} query string with leading `?`, or empty string
 */
export const buildFilterParams = (filters = {}, extra = {}) => {
  const params = new URLSearchParams()

  const appendList = (key, values) => {
    if (Array.isArray(values)) {
      values.forEach((v) => {
        if (v !== null && v !== undefined && v !== '') {
          params.append(key, String(v))
        }
      })
    }
  }

  appendList('statuses', filters.statuses)
  appendList('priorities', filters.priorities)
  appendList('assigneeIds', filters.assigneeIds)
  appendList('labelIds', filters.labelIds)
  appendList('releaseIds', filters.releaseIds)

  Object.entries(extra).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') {
      params.append(k, String(v))
    }
  })

  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

// ============================================================================
// OVERVIEW (EXECUTION SUMMARY) ENDPOINTS
// ============================================================================

export const fetchProjectExecutionSummary = async (jiraProjectId) => {
  const response = await apiClient.get(`/reports/execution-summary/project/${jiraProjectId}`)
  return response.data
}

export const fetchReleaseExecutionSummary = async (releaseId) => {
  const response = await apiClient.get(`/reports/execution-summary/release/${releaseId}`)
  return response.data
}

export const fetchScenarioExecutionSummary = async (testScenarioId, includeRunBreakdown = true) => {
  const query = buildFilterParams({}, { includeRunBreakdown })
  const response = await apiClient.get(`/reports/execution-summary/scenario/${testScenarioId}${query}`)
  return response.data
}

export const fetchAllReleasesSummary = async (jiraProjectId) => {
  const response = await apiClient.get(`/reports/execution-summary/project/${jiraProjectId}/releases`)
  return response.data
}

// ============================================================================
// TREND ENDPOINTS
// ============================================================================

/**
 * Fetch time-bucketed execution trend data.
 *
 * Response: { bucketUnit: 'DAY'|'WEEK', buckets: [{ bucketStart, statusCounts, totalExecutions, passRate }, ...] }
 */
export const fetchTrends = async (level, id, filters = {}) => {
  const query = buildFilterParams(filters)
  const response = await apiClient.get(`/reports/trends/${level}/${id}${query}`)
  return response.data
}

// ============================================================================
// DEFECT ANALYTICS
// ============================================================================

export const fetchDefectAnalytics = async (level, id, filters = {}) => {
  const query = buildFilterParams(filters)
  const response = await apiClient.get(`/reports/defects/${level}/${id}${query}`)
  return response.data
}

// ============================================================================
// TESTER PRODUCTIVITY (PEOPLE)
// ============================================================================

export const fetchTesterProductivity = async (level, id, filters = {}) => {
  const query = buildFilterParams(filters)
  const response = await apiClient.get(`/reports/people/${level}/${id}${query}`)
  return response.data
}

// ============================================================================
// TRACEABILITY
// ============================================================================

export const fetchTraceability = async (jiraProjectId, filters = {}) => {
  const query = buildFilterParams(filters)
  const response = await apiClient.get(`/reports/traceability/project/${jiraProjectId}${query}`)
  return response.data
}

// ============================================================================
// TOP FAILING (RELEASE SCOPE)
// ============================================================================

export const fetchTopFailing = async (releaseId, filters = {}) => {
  const query = buildFilterParams(filters)
  const response = await apiClient.get(`/reports/top-failing/release/${releaseId}${query}`)
  return response.data
}

// ============================================================================
// SCENARIO HISTORY
// ============================================================================

export const fetchScenarioHistory = async (scenarioId) => {
  const response = await apiClient.get(`/reports/scenario/${scenarioId}/history`)
  return response.data
}

// ============================================================================
// DRILL-DOWN
// ============================================================================

/**
 * Fetch the list of test case executions matching a scope + criteria.
 *
 * criteria: { level: 'project'|'release'|'scenario', id, status?, priority?, ... }
 */
export const fetchDrillDown = async (criteria) => {
  const { level, id, ...rest } = criteria
  const params = new URLSearchParams()
  Object.entries(rest).forEach(([k, v]) => {
    if (Array.isArray(v)) {
      v.forEach((item) => params.append(k, String(item)))
    } else if (v !== null && v !== undefined && v !== '') {
      params.append(k, String(v))
    }
  })
  const qs = params.toString()
  const response = await apiClient.get(
    `/reports/drill-down/${level}/${id}${qs ? `?${qs}` : ''}`
  )
  return response.data
}

// ============================================================================
// EXPORT (PDF / EXCEL)
// ============================================================================

/**
 * Request a server-rendered PDF or Excel export.
 *
 * Returns a Blob. The caller is expected to wrap it into a File / download link.
 */
export const fetchReportExport = async (level, id, format, filters = {}) => {
  const query = buildFilterParams(filters, { format })
  const response = await apiClient.get(`/reports/export/${level}/${id}${query}`, {
    responseType: 'blob'
  })
  return response.data
}
